import type { BuildLab, ClassifyLab, OrderLab, SpotLab } from "../data/labTypes";

export interface LabResult {
  score: number; // 0–100
  /** Chi tiết theo từng mục (true = đúng). */
  detail: boolean[];
  error?: string;
}

const pct = (ok: number, total: number) => (total ? Math.round((ok / total) * 100) : 0);

/** userOrder: danh sách chỉ số bước (theo thứ tự ĐÚNG gốc) mà người dùng sắp xếp. */
export function gradeOrder(lab: OrderLab, userOrder: number[]): LabResult {
  const size = lab.steps.length;
  if (userOrder.length !== size || new Set(userOrder).size !== size || userOrder.some((step) => !Number.isInteger(step) || step < 0 || step >= size)) {
    return { score: 0, detail: userOrder.map(() => false) };
  }
  const position = new Map<number, number>();
  userOrder.forEach((step, pos) => {
    if (Number.isInteger(step) && step >= 0 && step < size && !position.has(step)) position.set(step, pos);
  });
  const totalPairs = (size * (size - 1)) / 2;
  let correctPairs = 0;
  // Detail is aligned to the displayed order. A displayed step is correct when
  // its relative order against every other step is correct.
  const detail = userOrder.map((step, displayedPos) => {
    if (!position.has(step)) return false;
    let allPairsCorrect = true;
    for (const other of userOrder) {
      if (other === step || !position.has(other)) continue;
      const shouldComeBefore = step < other;
      const doesComeBefore = (position.get(step) as number) < (position.get(other) as number);
      if (shouldComeBefore === doesComeBefore) correctPairs++;
      else allPairsCorrect = false;
    }
    return allPairsCorrect && position.get(step) === displayedPos;
  });
  // Each pair is visited from both endpoints above.
  return { score: totalPairs ? pct(correctPairs / 2, totalPairs) : 100, detail };
}

export function gradeClassify(lab: ClassifyLab, choices: (number | undefined)[]): LabResult {
  const detail = lab.items.map((it, i) => choices[i] === it.bucket);
  return { score: pct(detail.filter(Boolean).length, lab.items.length), detail };
}

/** detail theo từng dòng: true nếu đánh giá dòng đó đúng (chọn đúng dòng lỗi, hoặc bỏ qua dòng không lỗi). */
export function gradeSpot(lab: SpotLab, selected: number[]): LabResult {
  const bugs = new Set(lab.bugLines);
  const sel = new Set(selected);
  const detail = lab.lines.map((_, i) => bugs.has(i) === sel.has(i));
  const hits = lab.bugLines.filter((i) => sel.has(i)).length;
  const falsePos = selected.filter((i) => !bugs.has(i)).length;
  const score = Math.max(0, Math.round(((hits - falsePos) / lab.bugLines.length) * 100));
  return { score, detail };
}

export function gradeBuild(lab: BuildLab, raw: string): LabResult {
  let parsed: unknown = raw;
  if (lab.format === "json") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return {
        score: 0,
        detail: lab.checks.map(() => false),
        error: `JSON không hợp lệ / Invalid JSON: ${e instanceof Error ? e.message : String(e)}`,
      };
    }
  }
  const detail = lab.checks.map((c) => {
    try {
      return c.test(parsed, raw);
    } catch {
      return false;
    }
  });
  return { score: pct(detail.filter(Boolean).length, lab.checks.length), detail };
}
